import { useCallback, useEffect, useMemo } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useMatch, useNavigate } from 'react-router-dom'

import { useCurrentUser } from 'modules/User/useCurrentUser'

import { countryCodeVar, labelCodeIdVar, spaceIdVar } from 'modulesCommon/store'
import {
  COUNTRY_CODE_PERSIST_KEY,
  SPACE_ID_PERSIST_KEY,
  useSpaces,
  useSwitchToProjectZone,
  useZonesList,
  ZONE_ID_PERSIST_KEY
} from '.'
import { useLoading } from 'shared/hooks'
import { useZones } from './useZones'
import { useZoneManager } from '../../shared/hooks/useZoneManager'


// TODO: Create persist data strategy
function getPersistValue(key: string): string | undefined {
  const value = localStorage.getItem(key)
  if (typeof value === 'string' && value.length) return value
  return undefined
}

export const useZoneMenu = () => {
  const { user } = useCurrentUser()
  const { getZoneById } = useZones()
  const { buildZoneUrl, replaceZoneInUrl, replaceCountryCodeInUrl, currentZone, currentCountryCode } = useZoneManager()
  const projectPageMatch = useMatch('/:zone/:countryCode/projects/:projectId/*')

  const currentLabelId = useReactiveVar(labelCodeIdVar)
  const currentSpaceId = useReactiveVar(spaceIdVar)

  const { redirectToProject } = useSwitchToProjectZone()


  const navigate = useNavigate()
  const {
    zoneLoading,
    getCountryOptions: getZoneCountryOptions,
    zoneOptions
  } = useZonesList()
  const { getSpaceById, getCountryOptions: getSpaceCountryOptions, spacesOptions, setSpace } = useSpaces()

  /**
   * TODO: Should be moved to User env
   * start of section
   */
  useEffect(
    () => {
      if (typeof user === 'undefined') return
      if (currentSpaceId || currentLabelId) return
      if (typeof user.space_id === 'string') {
        const persistSpaceId = getPersistValue(SPACE_ID_PERSIST_KEY)
        spaceIdVar(persistSpaceId || user.space_id)
      } else if (typeof user.label_id === 'string') {
        const persistZoneId = getPersistValue(ZONE_ID_PERSIST_KEY)
        labelCodeIdVar(persistZoneId || user.label_id)
      }
      const persistCountryCode = getPersistValue(COUNTRY_CODE_PERSIST_KEY)
      countryCodeVar(persistCountryCode || user.country_code)
    },
    [currentLabelId, currentSpaceId, user]
  )
  const onSelectLabel = useCallback<(label_id: string) => void>(
    (label_id) => {
      const zone = getZoneById(+label_id)
      if (!zone) return console.error('Can not redirect to zone', zone)
      const isProjectPage = projectPageMatch !== null

      if (isProjectPage) {
        navigate(buildZoneUrl('/projects',zone))
      } else {
        navigate(replaceZoneInUrl(zone))
      }
    }, [getZoneById, projectPageMatch, navigate, buildZoneUrl, replaceZoneInUrl]
  )
  const onSelectCountry = useCallback<(countryCode: string) => void>(
    (countryCode) => {
      navigate(replaceCountryCodeInUrl(countryCode))
    }, [navigate, replaceCountryCodeInUrl]
  )

  const onSelectSpace = useCallback<(space_id: string, country_code?: string) => void>(
    (space_id, country_code) => {
      setSpace(space_id, country_code)
      redirectToProject()
    }, [redirectToProject, setSpace]
  )

  /**
   * end of section
   */

  const currentSpace = useMemo(
    () => getSpaceById(currentSpaceId),
    [getSpaceById, currentSpaceId]
  )

  const countryOptions = useMemo(
    () => {
      if (currentZone) {
        return getZoneCountryOptions(currentZone.id.toString())
      } else if (currentSpaceId?.length) {
        return getSpaceCountryOptions(currentSpaceId)
      }
    },
    [currentZone, currentSpaceId, getSpaceCountryOptions, getZoneCountryOptions]
  )

  useLoading({ isLoading: zoneLoading })

  return {
    countryCode: currentCountryCode,
    zoneOptions,
    countryOptions,
    spacesOptions,
    onSelectSpace,
    onSelectLabel,
    currentSpace,
    currentZone,
    onSelectCountry
  }
}
